import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Settings, Wrench, Users, FileText, LayoutList, LayoutDashboard, Menu, X, UserPlus, LogOut } from "lucide-react";
import { Toaster } from "react-hot-toast";

import NovaOS from "./pages/NovaOS";
import EditarOS from "./pages/EditarOS";
import Relatorio from "./pages/Relatorio";
import TecnicosScreen from "./pages/Tecnicos";
import ServicosScreen from "./pages/Servicos";
import ConfiguracoesScreen from "./pages/Configuracoes";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Usuarios from "./pages/Usuarios";

const SidebarLink = ({ to, icon, label, onClick }) => {
  const location = useLocation();
  const active = location.pathname === to || (to === '/relatorio' && location.pathname.includes('/editar'));
  
  return (
    <Link to={to} onClick={onClick} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${active ? "bg-white/10 text-white font-medium" : "hover:bg-white/5 hover:text-white"}`}>
      {icon} {label}
    </Link>
  );
};

export default function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const closeMenu = () => setIsMobileMenuOpen(false);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('@up2_user');
    if (storedUser) setUser(JSON.parse(storedUser));
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('@up2_token', token);
    localStorage.setItem('@up2_user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('@up2_token');
    localStorage.removeItem('@up2_user');
    setUser(null);
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Carregando...</div>;

  if (!user) {
    return (
      <>
        <Toaster position="top-right" />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans">
        <div className="md:hidden flex items-center justify-between bg-slate-900 p-4 text-white z-20 shadow-md">
          <div className="flex items-center gap-2 font-bold text-xl">UP2 - OS</div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-1 focus:outline-none hover:bg-white/10 rounded-md transition-colors">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="fixed inset-0 bg-black/60 z-30 md:hidden animate-in fade-in" onClick={closeMenu} />
        )}

        <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 text-slate-300 flex flex-col transform transition-transform duration-300 ease-in-out shadow-2xl md:shadow-none md:relative md:translate-x-0 ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="p-6 hidden md:block border-b border-white/5">
            <h1 className="text-2xl font-bold text-white tracking-tight">UP2 - OS</h1>
            <p className="text-sm text-slate-500">Gestor de Ordens de Serviço</p>
          </div>

          <div className="p-4 md:hidden flex justify-between items-center border-b border-white/5 mb-2">
            <div>
              <h2 className="text-lg font-bold text-white">Menu</h2>
              <p className="text-xs text-slate-500">Navegação do Sistema</p>
            </div>
            <button onClick={closeMenu} className="p-2 hover:bg-white/10 rounded-full"><X size={20} className="text-slate-400" /></button>
          </div>

          <nav className="flex-1 px-4 space-y-2 mt-4 md:mt-4 overflow-y-auto">
            <SidebarLink onClick={closeMenu} to="/" icon={<LayoutDashboard size={18} />} label="Dashboard" />
            <SidebarLink onClick={closeMenu} to="/relatorio" icon={<LayoutList size={18} />} label="Relatórios" />
            <SidebarLink onClick={closeMenu} to="/nova" icon={<FileText size={18} />} label="Nova OS" />
            <SidebarLink onClick={closeMenu} to="/tecnicos" icon={<Users size={18} />} label="Técnicos" />
            <SidebarLink onClick={closeMenu} to="/servicos" icon={<Wrench size={18} />} label="Serviços" />
            {user.role === 'ADMIN' && (
              <>
                <SidebarLink onClick={closeMenu} to="/usuarios" icon={<UserPlus size={18} />} label="Usuários do Sistema" />
                <SidebarLink onClick={closeMenu} to="/config" icon={<Settings size={18} />} label="Configurações" />
              </>
            )}
          </nav>

          <div className="p-4 mt-auto border-t border-white/5 bg-slate-950/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center text-white font-bold shrink-0">
                {user.nome.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-bold text-white truncate">{user.nome}</p>
                <p className="text-xs text-slate-400 tracking-wider">{user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}</p>
              </div>
              <button onClick={handleLogout} title="Sair do Sistema" className="p-2 text-slate-400 hover:text-red-500 hover:bg-white/5 rounded-md transition-colors">
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 p-4 md:p-8 overflow-y-auto relative">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/relatorio" element={<Relatorio />} />
              <Route path="/nova" element={<NovaOS />} />
              <Route path="/editar/:id" element={<EditarOS />} />
              <Route path="/tecnicos" element={<TecnicosScreen />} />
              <Route path="/servicos" element={<ServicosScreen />} />
              
              {user.role === 'ADMIN' && (
                <>
                  <Route path="/usuarios" element={<Usuarios />} />
                  <Route path="/config" element={<ConfiguracoesScreen />} />
                </>
              )}
            </Routes>
          </div>
        </main>

      </div>
    </BrowserRouter>
  );
}